import type { Maybe } from '@/helpers/types';

import { CollectionType } from '@/enums/collection-type';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { Route } from 'vue-router';
import { RouterMeta } from '@/routing/config';
import { OnboardingStatus } from '@/enums/onboarding-status';

import { getPageType } from '@/helpers/tracking/tracking-helper';
import { useUserStore } from '@/stores/pinia/useUserStore';
import {
	GetUrlMetadataDocument,
	GetUrlMetadataQuery,
	GetUrlMetadataQueryVariables,
} from '@/graphql/query/GetUrlMetadata.query';
import { Domain } from '@/routing/helpers/subdomain-helper';
import { ApolloProvider } from 'vue-apollo';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { ObjectType } from '@/@types/graphql-types';

// Type alias for URL metadata from GraphQL, extended with routing store additions
export type UrlMetadata = GetUrlMetadataQuery['urlV2'] & {
	allHrefLangTags?: Array<{ href: string; hrefLang: string; locale: string }>;
};

export type ListsCollectionType =
	| CollectionType.MY_LISTS
	| CollectionType.PUBLIC_LISTS
	| CollectionType.TV_SHOW_TRACKING
	| CollectionType.MY_RENTALS;

export type RoutingState = {
	activeRoute: Route | null;
	// this is used to fix the lazy loading of components with tabs.
	// [tab]: true when activated once
	activatedTabs: Record<string, boolean>;
	lastActiveCollectionType: CollectionType | null;
	lastActiveListsCollectionType: ListsCollectionType | null;
	lastRoute: Route | null;
	lastUrlMetadata: UrlMetadata | null;
	pageType: string;
	scrollPositions: Partial<Record<RouterMeta['tab'], number>>;
	urlMetadata: UrlMetadata | null;
	// TODO We're assuming implicitly this would only be called after promise is registered. That should not be the case.
	// An event that other modules can register to - so they get the data when it's there.
	urlMetadataPromise: Promise<UrlMetadata> | null;
};

export const useRoutingStore = defineStore('routing', () => {
	const languageStore = useLanguageStore();

	const activeDomain = ref<Domain>(Domain.MAIN);
	const activeRoute = ref<Route | null>(null);
	// this is used to fix the lazy loading of components with tabs.
	// [tab]: true when activated once
	const activatedTabs = ref<Record<string, boolean>>({});

	const pageType = ref('VIEW_HOME'); // default url

	const lastActiveCollectionTypeState = ref<CollectionType | null>(null);

	const lastActiveListsCollectionType = ref<ListsCollectionType | null>(null);

	const lastRoute = ref<Route | null>(null);

	const lastUrlMetadata = ref<UrlMetadata | null>(null);

	const scrollPositions = ref<Partial<Record<RouterMeta['tab'], number>>>({});

	const urlMetadataState = ref<UrlMetadata | null>(null);

	// TODO We're assuming implicitly this would only be called after promise is registered. That should not be the case.
	// An event that other modules can register to - so they get the data when it's there.
	const urlMetadataPromise = ref<Promise<UrlMetadata> | null>(null);

	const activeCollectionType = computed<CollectionType | null>(() => {
		return activeRoute.value ? (activeRoute.value.meta || {}).collectionType : null;
	});

	const currentPageType = computed(() => {
		return pageType.value;
	});

	const lastActiveCollectionType = computed(() => {
		const currentCollectionType = activeCollectionType.value;
		if (currentCollectionType) {
			return currentCollectionType;
		}

		if (lastActiveCollectionTypeState.value) {
			return lastActiveCollectionTypeState.value;
		}

		// This is a failsafe, it should never land here. Gotta investigate or rewrite this
		return CollectionType.GLOBAL;
	});

	const queryParams = computed<Route['query']>(() => {
		if (activeRoute.value) {
			// TODO Unsure if casting makes sense here.
			return activeRoute.value.query as Dictionary<string | string[]>;
		} else {
			return {};
		}
	});

	const urlMetadata = computed<RoutingState['urlMetadata']>(() => {
		const urlMetadata = (urlMetadataState.value || {}) as UrlMetadata;
		const heading1 = urlMetadata.heading1;
		return {
			...urlMetadata,
			...{ heading1 },
		};
	});

	async function fetchUrlMetadata(payload: string | { url: string; apolloProvider: ApolloProvider }) {
		const url = typeof payload === 'string' ? payload : payload.url;
		const apolloProvider = typeof payload === 'object' ? payload.apolloProvider : null;
		let path = url.split('?')[0];
		if (path !== '/' && !path.includes('shared')) {
			path = path.replace(/\/$/, ''); // remove trailing backslash
			try {
				let promise: Promise<GetUrlMetadataQuery | { urlV2: null }>;

				// Check if Apollo provider is available and has defaultClient
				if (apolloProvider?.defaultClient) {
					// Use GraphQL query via Apollo
					promise = apolloProvider.defaultClient
						.query<GetUrlMetadataQuery, GetUrlMetadataQueryVariables>({
							query: GetUrlMetadataDocument,
							variables: {
								fullPath: path,
								site: activeDomain.value,
							},
						})
						.then(({ data }) => data);
				} else {
					// Apollo provider is not available - skip GraphQL query
					promise = Promise.resolve({ urlV2: null });
				}

				setUrlMetadataPromise(promise);
				const result = await promise;
				setLastUrlMetadata(urlMetadataState.value);
				setUrlMetadata(result?.urlV2);
			} catch (err) {
				console.error('[RoutingStore] fetchUrlMetadata', url, err);
				setUrlMetadataPromise(Promise.resolve());
			}

			// @todo netflix 4k exception
			// @todo maybe set customCanonical to meta.store.ts state?
		} else {
			const userStore = useUserStore();
			const onboardingStatus = userStore.onboardingStatus;
			const technicalName =
				activeDomain.value === Domain.EDITORIAL
					? 'VIEW_HOME_GUIDES'
					: onboardingStatus === OnboardingStatus.GLOBAL
					? 'VIEW_HOME_GLOBAL'
					: 'VIEW_HOME';
			const homeMockMeta: UrlMetadata = {
				id: 'home',
				urlID: 0,
				locale: languageStore.locale,
				objectType: ObjectType.Page,
				fullPath: '/',
				site: activeDomain.value,
				metaTitle: 'JustWatch - The Streaming Guide',
				metaDescription: 'All your streaming services in one app.',
				metaKeywords: '',
				page: { technicalName },
				heading1: null,
				heading2: null,
				metaRobots: '',
				hrefLangTags: [],
				allHrefLangTags: [],
			};

			setLastUrlMetadata(urlMetadataState.value);
			setUrlMetadata(homeMockMeta);
			setUrlMetadataPromise(Promise.resolve());
		}
		pageType.value = getPageType(urlMetadataState.value);
	}

	function setActiveDomain(domain: Domain) {
		activeDomain.value = domain;
	}
	function setUrlMetadataPromise(promise: Promise<any>) {
		urlMetadataPromise.value = promise;
	}

	function setLastUrlMetadata(urlMetadata: UrlMetadata | null) {
		lastUrlMetadata.value = urlMetadata;
	}

	function setUrlMetadata(metadata?: Maybe<UrlMetadata>) {
		const metaRobots = metadata?.metaRobots ?? '';
		const hasNoIndex = metaRobots.includes('noindex');
		// make sure we don't include urls that have noindex
		const hrefLangTags = (metadata?.hrefLangTags || []).filter(tag => !tag.hasNoindex && !hasNoIndex);

		urlMetadataState.value = {
			...((metadata ?? {}) as UrlMetadata),
			hrefLangTags,
			allHrefLangTags: metadata?.hrefLangTags || [],
		};
	}

	async function setActiveRoute({ activeRoute }: { activeRoute: Route }) {
		const meta = { ...activeRoute.meta };

		// exclude 'matched' that causes circular dependency
		/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
		const { matched, ...route } = activeRoute;
		updateCurrentRoute({ ...route, path: decodeURI(route.path), meta });
		activateTab(activeRoute?.meta?.tab);
	}

	function updateCurrentRoute(route: any) {
		lastRoute.value = activeRoute.value ? { ...activeRoute.value } : null;

		activeRoute.value = { ...route };
		// only set lastActiveCollectionType of the last route that had a collectionType
		if (route && (route.meta || {}).collectionType) {
			lastActiveCollectionTypeState.value = route.meta?.collectionType;
		}
	}

	function setLastActiveListsCollectionType(collectionType: ListsCollectionType) {
		lastActiveListsCollectionType.value = collectionType;
	}

	function activateTab(tab: string) {
		if (tab) {
			activatedTabs.value = {
				...activatedTabs.value,
				[tab]: true,
			};
		}
	}

	function setScrollPosition({ tab, y }: { tab: RouterMeta['tab']; y: number }) {
		if (tab) {
			scrollPositions.value = { ...scrollPositions.value, [tab]: y };
		}
	}

	return {
		activeRoute,
		activatedTabs,
		pageType,
		lastActiveCollectionTypeState,
		lastActiveListsCollectionType,
		lastRoute,
		lastUrlMetadata,
		scrollPositions,
		urlMetadataState,
		urlMetadataPromise,

		activeCollectionType,
		currentPageType,
		lastActiveCollectionType,
		queryParams,
		urlMetadata,
		activeDomain,

		fetchUrlMetadata,
		setActiveDomain,
		setActiveRoute,
		updateCurrentRoute,
		setLastActiveListsCollectionType,
		activateTab,
		setScrollPosition,
		setUrlMetadataPromise,
	};
});
